import { IconX } from '@tabler/icons-react'
import { Button, Empty, Spin } from 'antd'
import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import Radio from '@/components/fields/radio'
import { MediaSlider } from '@/components/shared/media-slider'
import MediaView from '@/components/shared/media-view'
import { LoadingType } from '@/constants/loading-type'
import { Media } from '@/model/media'
import { convertVaultResponseToMedias, VaultMediaTypeOptionValue } from '@/model/vault'
import { useAppDispatch } from '@/redux/utils'
import { readVaults, ReadVaultsRequest } from '@/redux/vault-actions'
import { changeRadioValues } from '@/utils/array'

import columnStyle from './column.module.css'
import s from './media-column.module.css'
import VaultMediaSearchBar from './vault-media-search-bar'

export interface MediaColumnProps {
  className?: string
  selectedMedias: Media[]
  excludedMediaUrls?: string[]
  onAdd?: () => void
  onSelectMedias: (medias: Media[]) => void
  maxSelection?: number
  creatorId: number
  enableSelection?: boolean
}

export const MAX_SELECTION = 40

export default function MediaColumn({
  className,
  creatorId,
  excludedMediaUrls = [],
  selectedMedias,
  onAdd,
  onSelectMedias,
  maxSelection = MAX_SELECTION,
  enableSelection = false,
}: MediaColumnProps) {
  const [responseSize, setResponseSize] = useState<number>(0)
  const [medias, setMedias] = useState<Media[]>([])
  const [loading, setLoading] = useState<number>(LoadingType.Idle)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [query, setQuery] = useState<ReadVaultsRequest>({
    creatorId,
    limit: 20,
    pageNumber: 0,
  })
  const [vaultMediaType, setVaultMediaType] = useState<VaultMediaTypeOptionValue>('all')

  const dispatch = useAppDispatch()

  const [sliderOpen, setSliderOpen] = useState(false)
  const [sliderIndex, setSliderIndex] = useState(0)

  const loadData = useCallback(
    async (query: ReadVaultsRequest) => {
      const loadMore = query.pageNumber > 1
      const existMedias = loadMore ? medias : []
      setLoading(loadMore ? LoadingType.More : LoadingType.Refresh)
      if (!loadMore) {
        setMedias([])
        setResponseSize(0)
        setHasMore(false)
      }
      setQuery(query)
      try {
        const response = await dispatch(readVaults(query)).unwrap()
        const newResponseSize = responseSize + response.type_vaults.length
        setMedias([...existMedias, ...response.type_vaults.map(convertVaultResponseToMedias).flat()])
        setResponseSize(newResponseSize)
        setHasMore(newResponseSize < response.total)
      } catch (error) {
        console.error('fetching vaults error: ', error)
      }
      setLoading(LoadingType.Idle)
    }, [dispatch, medias, responseSize],
  )

  const [sentryRef] = useInfiniteScroll({
    loading: loading > 0,
    hasNextPage: hasMore,
    onLoadMore: () => {
      void loadData({ ...query, pageNumber: query.pageNumber + 1 })
    },
  })

  useEffect(() => {
    if (creatorId !== query.creatorId) {
      const nextQuery = { ...query, pageNumber: 1, creatorId }
      void loadData(nextQuery)
    }
  }, [creatorId, loadData, query])

  const mediasToShow = useMemo(() => (medias
    .filter(({ type }) => vaultMediaType === 'all' || type === vaultMediaType)
    .filter(({ src }) => src && !excludedMediaUrls.includes(src))
  ), [excludedMediaUrls, medias, vaultMediaType])

  return (
    <div className={classNames(s.MediaColumn, className)}>
      <div className={s.MediaColumnHeader}>
        ALL MEDIA
      </div>
      <VaultMediaSearchBar
        mediaType={vaultMediaType}
        onMediaTypeChange={setVaultMediaType}
      />
      <div className={columnStyle.ColumnContent}>
        {mediasToShow.length > 0 ? (
          <div className={s.Medias}>
            {mediasToShow.map((item, index) => (
              <MediaView
                className={classNames(s.Media, {
                  [s.MediaChecked]: selectedMedias.some(({ src }) => src === item.src),
                })}
                key={index}
                media={item}
                isPreview
                onClick={() => {
                  setSliderIndex(index)
                  setSliderOpen(true)
                }}
                extra={
                  enableSelection && <Radio
                    value={selectedMedias.some(({ src }) => src === item.src)}
                    onChange={changeRadioValues({
                      value: item,
                      selectedValues: selectedMedias,
                      setSelectedValues: onSelectMedias,
                      maxSelectedValues: maxSelection - excludedMediaUrls.length,
                      stopPropagation: true,
                      compareFn: (a, b) => a.src === b.src,
                    })}
                    borderColor="white"
                    size={16}
                    className={s.Radio}
                  />
                }
              />
            ))}
          </div>
        ) : (!loading && <Empty className={s.Empty} />)}
        {(loading || hasMore) && (
          <div className={columnStyle.Loading} ref={sentryRef}>
            <Spin />
          </div>
        )}
      </div>
      <MediaSlider
        open={sliderOpen}
        setOpen={setSliderOpen}
        index={sliderIndex}
        medias={mediasToShow}
      />
      {selectedMedias.length > 0 && (
        <div className={s.Selection}>
          <div className={s.SelectionHead}>
            <IconX className={s.Clear} onClick={() => onSelectMedias([])} />
            {selectedMedias.length} / {maxSelection - excludedMediaUrls.length}{' '}
            Chosen
          </div>
          <Button type="primary" shape="round" size="small" onClick={onAdd}>
            Add
          </Button>
        </div>
      )}
    </div>
  )
}
